'use client';

import { useEffect, useState } from 'react';
import { cn } from '@shared/utils/utils';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Minus, Plus } from 'lucide-react';
import { useStepper } from './hooks';
import type { StepperInputProps } from './types';

/**
 * Componente StepperInput - Input numérico con botones de incremento/decremento
 *
 * Input especializado para valores numéricos con controles visuales para
 * incrementar y decrementar. Incluye validación automática de límites.
 *
 * Características:
 * - Botones de incremento/decremento
 * - Validación automática de límites (min/max)
 * - Soporte para pasos personalizados
 * - Estado visual de botones (habilitado/deshabilitado)
 * - Input directo con validación
 * - Accesibilidad completa
 * - Test IDs automáticos
 *
 * @param value - Valor actual del stepper
 * @param onChange - Función para actualizar el valor
 * @param min - Valor mínimo permitido (default: 0)
 * @param max - Valor máximo permitido (default: Infinity)
 * @param step - Paso de incremento/decremento (default: 1)
 * @param className - Clases CSS adicionales
 * @param disabled - Si el componente está deshabilitado
 * @param data-testid - Test ID personalizado
 */
const StepperInput = ({
  value,
  onChange,
  min = Number.NEGATIVE_INFINITY,
  max = Infinity,
  step = 1,
  className,
  disabled = false,
  allowNegative = true,
  'data-testid': testId = 'stepper-input',
}: StepperInputProps) => {
  // ============================================================================
  // HOOKS Y ESTADO
  // ============================================================================

  const effectiveMin = allowNegative && min >= 0 ? Number.NEGATIVE_INFINITY : min;
  const emptyBlurValue = Number.isFinite(effectiveMin) && effectiveMin > 0 ? effectiveMin : 0;

  const {
    value: clampedValue,
    canIncrement,
    canDecrement,
    increment,
    decrement,
    setValue,
  } = useStepper(value, onChange, effectiveMin, max, step);

  // Estado local como string para permitir borrar completamente el contenido
  const [inputValue, setInputValue] = useState<string>(String(clampedValue));

  // Sincronizar el valor local cuando cambie el valor clampado externo
  useEffect(() => {
    setInputValue(String(clampedValue));
  }, [clampedValue]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Maneja cambios en el input directo
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let next = e.target.value;

    if (!allowNegative) {
      // Solo dígitos: bloquea "-", "e", decimales, etc.
      next = next.replace(/\D/g, '');
    }

    setInputValue(next);
  };

  /**
   * Selecciona todo el contenido cuando el input recibe foco
   * para que el usuario pueda sobrescribir el valor actual fácilmente.
   */
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  /**
   * Al perder el foco, aplicamos el valor escrito al estado numérico
   */
  const handleInputBlur = () => {
    const trimmed = inputValue.trim();

    // Si el usuario deja el input vacío, normalizar al mínimo permitido (o 0)
    if (trimmed === '') {
      setValue(emptyBlurValue);
      setInputValue(String(emptyBlurValue));
      return;
    }

    const parsed = Number(trimmed);

    if (!Number.isNaN(parsed)) {
      setValue(parsed);
      setInputValue(String(parsed));
    } else {
      // Si no es un número válido, volvemos al valor clampado actual
      setInputValue(String(clampedValue));
    }
  };

  /**
   * Permitir confirmar valor con Enter
   */
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!allowNegative && (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E' || e.key === '.')) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      handleInputBlur();
    }
  };

  /**
   * Maneja el incremento con validación de estado
   */
  const handleIncrement = () => {
    if (!disabled && canIncrement) {
      increment();
    }
  };

  /**
   * Maneja el decremento con validación de estado
   */
  const handleDecrement = () => {
    if (!disabled && canDecrement) {
      decrement();
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div
      className={cn(
        'flex items-center border border-card-border bg-card-bg rounded px-2 py-1 w-full h-10',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
      data-testid={testId}
    >
      {/* Botón de decremento */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleDecrement}
        disabled={disabled || !canDecrement}
        className="h-6 w-6 shrink-0"
        data-testid={`${testId}-decrement`}
        aria-label="Decrementar valor"
      >
        <Minus className="w-4 h-4" />
      </Button>

      {/* Input central */}
      <Input
        type="number"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        disabled={disabled}
        min={allowNegative ? undefined : min}
        max={max}
        step={step}
        className="flex-grow w-full text-center border-none focus-visible:ring-0 focus:outline-none bg-transparent px-2"
        data-testid={`${testId}-input`}
        aria-label={`Valor numérico entre ${effectiveMin} y ${max}`}
      />

      {/* Botón de incremento */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleIncrement}
        disabled={disabled || !canIncrement}
        className="h-6 w-6 shrink-0"
        data-testid={`${testId}-increment`}
        aria-label="Incrementar valor"
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default StepperInput;
